import { Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { osirisTokens } from '@/theme/tokens';
// The design's own topo-contour texture (Figma fill unsplash:dbZW3HauxrE) —
// the aerial photo read as a different image (Figma To Do 28458:21481)
import emptyBgImage from '@/assets/empty_bg.jpg';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
}

interface EmptyStateProps {
  /** Self-contained illustration (title + description are baked into the SVG). */
  image: string;
  /** Optional primary call-to-action rendered below the illustration. */
  action?: EmptyStateAction;
  /** Renders the dimmed aerial background + grid behind the illustration (full-page states only). */
  background?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}

export function EmptyState({ image, action, background = false, className, sx }: EmptyStateProps) {
  return (
    <Box
      className={`empty-state${className ? ' ' + className : ''}`}
      sx={[
        {
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          overflow: 'hidden',
          borderRadius: '2px',
          // Bg/bg-page + Bg/bg-surface, per the Figma "Empty" component
          bgcolor: osirisTokens.bg.page,
          backgroundImage: `linear-gradient(${osirisTokens.bg.surface}, ${osirisTokens.bg.surface})`,
          ...(background
            ? // Full-page states start 200px below the header instead of centring
              { justifyContent: 'flex-start', pt: '200px', pb: 7 }
            : { justifyContent: 'center', py: 7 }),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {background && (
        <>
          {/* Topo texture at 15% + bg-page overlay, per the Figma background-assets frame */}
          <Box
            component="img"
            src={emptyBgImage}
            alt=""
            className="empty-state-bg"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.15,
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(26, 26, 26, 0.5)' }} />
          <Box
            className="empty-state-grid"
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              // neutral/800 cell borders on a 176px grid, per the Figma frame
              backgroundImage:
                'linear-gradient(#262626 1px, transparent 1px), linear-gradient(90deg, #262626 1px, transparent 1px)',
              backgroundSize: '176px 176px',
            }}
          />
        </>
      )}
      <Box
        component="img"
        src={image}
        alt=""
        className="empty-state-image"
        sx={{ position: 'relative', maxWidth: '100%', height: 'auto', display: 'block' }}
      />
      {action && (
        <Box
          className="empty-state-action"
          onClick={action.onClick}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            height: 40,
            px: 2,
            borderRadius: '2px',
            cursor: 'pointer',
            '&:hover': { bgcolor: osirisTokens.bg.surface },
          }}
        >
          {action.icon ?? <Add sx={{ fontSize: 20, color: osirisTokens.text.active }} />}
          <Typography
            sx={{
              fontFamily: osirisTokens.fonts.mono,
              fontSize: 11,
              letterSpacing: '0.02em',
              color: osirisTokens.text.active,
            }}
          >
            {action.label}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
